import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useNavigationContext } from "@/hooks/useNavigationContext";
import { cn } from "@/lib/utils";
import { buyerNavConfig } from "@/config/navigation/buyerNav";
import { supplierNavConfig } from "@/config/navigation/supplierNav";
import { adminNavConfig } from "@/config/navigation/adminNav";
import type { NavConfig, NavItemConfig } from "@/types/navigation";
import { MobileMoreSheet } from "./MobileMoreSheet";

export function MobileBottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { role } = useUserRole();
  const navContext = useNavigationContext();
  const [moreOpen, setMoreOpen] = useState(false);

  // Hide on auth pages
  if (location.pathname === "/auth" || location.pathname === "/" || location.pathname === "/home") {
    return null;
  }

  const personaConfig: NavConfig =
    role === "supplier"
      ? supplierNavConfig
      : role === "admin"
      ? adminNavConfig
      : buyerNavConfig;

  const getBadgeValue = (item: NavItemConfig) => {
    if (!item.badge) return 0;
    const metricValue = navContext[item.badge.metric];
    return typeof metricValue === "number" ? metricValue : 0;
  };

  const handleNavigate = (path: string) => {
    if (path === "#more") {
      setMoreOpen(true);
      return;
    }
    navigate(path);
  };

  const renderPersonaNav = () => (
    <div className="pb-safe flex h-16 items-center justify-around px-3">
      {personaConfig.mobileBottomNav.map((item) => {
        const Icon = item.icon;
        const isActive =
          item.href !== "#more" &&
          (location.pathname === item.href ||
            location.pathname.startsWith(item.href + "/"));
        const badgeCount = getBadgeValue(item);

        return (
          <button
            key={item.href}
            onClick={() => handleNavigate(item.href)}
            aria-label={item.label}
            aria-current={isActive ? "page" : undefined}
            className={cn(
              "flex flex-col items-center justify-center gap-1 min-w-0 flex-1 h-full transition-colors",
              "active:bg-muted/50",
              isActive ? "text-primary bg-primary/5" : "text-muted-foreground"
            )}
            style={{ minHeight: "44px" }}
          >
            <div className="relative">
              <Icon className={cn("h-5 w-5", isActive && "text-primary")} />
              {badgeCount > 0 && item.type !== "more" && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-xs font-semibold text-primary-foreground">
                  {badgeCount > 9 ? "9+" : badgeCount}
                </span>
              )}
            </div>
            <span
              className={cn(
                "text-sm md:text-base font-medium truncate max-w-full",
                isActive && "text-primary"
              )}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      <nav className="safe-area-bottom fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background md:hidden">
        {renderPersonaNav()}
      </nav>
      <MobileMoreSheet
        open={moreOpen}
        onOpenChange={setMoreOpen}
        config={personaConfig}
        onNavigate={(href) => {
          setMoreOpen(false);
          navigate(href);
        }}
      />
    </>
  );
}
